import { HttpClient } from '@adapters/HttpClient';
import { PreInvoice } from '../models/PreInvoices';

export interface PreInvoicesFilters {
  searchFilter?: { filters: any[]; logic: string };
  search?: string;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

export interface ExportPreInvoicesPayload {
  advanced_filters?: {
    filters: Array<{
      column: string;
      operator: string;
      value?: any;
    }>;
    logic: string;
  };
  sort_by?: string;
  sort_direction?: 'asc' | 'desc';
}

export interface ExportPreInvoicesResponse {
  url_file: string;
}

export interface PreInvoicesPage {
  preInvoices: PreInvoice[];
  nextPage?: number;
  hasMore: boolean;
  total: number;
}

function buildAdvancedFiltersParams(searchFilter: {
  filters: any[];
  logic: string;
}) {
  const params: Record<string, any> = {};
  if (searchFilter.filters && Array.isArray(searchFilter.filters)) {
    searchFilter.filters.forEach((f, i) => {
      params[`advanced_filters[filters][${i}][column]`] = f.column;
      params[`advanced_filters[filters][${i}][operator]`] = f.operator;
      // Solo enviar value si existe (para operadores is_null / is_not_null no debe enviarse)
      if (
        f.value !== undefined &&
        f.value !== null &&
        !(Array.isArray(f.value) && f.value.length === 0)
      ) {
        params[`advanced_filters[filters][${i}][value]`] = f.value;
      }
    });
  }
  if (searchFilter.logic) {
    params['advanced_filters[logic]'] = searchFilter.logic;
  }
  return params;
}

// ?? Construye los par�metros para exportaci�n exactamente como el GET de PreInvoices (lista)
function buildExportParamsFromPayload(payload: ExportPreInvoicesPayload) {
  const params: Record<string, any> = {};

  if (payload.advanced_filters) {
    const af = buildAdvancedFiltersParams(payload.advanced_filters as any);
    Object.assign(params, af);
  }

  const sortBy = payload.sort_by || 'id';
  const sortDirection = payload.sort_direction || 'desc';
  params['sorting[column]'] = sortBy;
  params['sorting[direction]'] = sortDirection;

  if (payload.status_ids !== undefined && payload.status_ids !== null) {
    params.status_ids = payload.status_ids;
  }

  return params;
}

export class PreInvoicesApiAdapter {
  constructor(private httpClient: HttpClient) {}

  async getPreInvoicesPaginated(filters: PreInvoicesFilters = {}): Promise<PreInvoicesPage> {
    const {
      searchFilter,
      search,
      sortBy = 'id',
      sortDirection = 'desc',
      page = 1,
      limit = 15,
    } = filters;

    const params: any = {
      ...(sortBy && { 'sorting[column]': sortBy }),
      ...(sortDirection && { 'sorting[direction]': sortDirection }),
      page,
      per_page: limit,
      ...(search ? { search } : {}),
      ...(searchFilter ? buildAdvancedFiltersParams(searchFilter) : {}),
    };

    // Solo mostrar loading global para la primera página
    const showGlobalLoading = page === 1;
    const response = await this.httpClient.get<any>(
      '/invoices/pre-invoices',
      params,
      {},
      showGlobalLoading
    );

    return {
      preInvoices: response.data,
      nextPage:
        response.meta.currentPage < response.meta.lastPage
          ? response.meta.currentPage + 1
          : undefined,
      hasMore: response.meta.currentPage < response.meta.lastPage,
      total: response.meta.total,
    };
  }

  async getAll(): Promise<PreInvoice[]> {
    const response = await this.getPreInvoicesPaginated();
    return response.preInvoices;
  }

  async getPreInvoiceById(id: number): Promise<PreInvoice> {
    const response = await this.httpClient.get<PreInvoice>(`/invoices/pre-invoices/${id}`);
    return response;
  }

  async exportPreInvoices(
    payload: ExportPreInvoicesPayload
  ): Promise<ExportPreInvoicesResponse> {
    const params = buildExportParamsFromPayload(payload);
    return await this.httpClient.post('/invoices/pre-invoices/export', params);
  }

  /**
   * Combinar múltiples prefacturas en una sola
   * POST /api/v1/invoices/pre-invoices/merge
   * @param preInvoiceIds - Array de al menos 2 IDs de prefacturas (DRAFT o REVIEW)
   * @returns La nueva prefactura combinada
   */
  async mergePreInvoices(preInvoiceIds: number[]): Promise<PreInvoice> {
    const response = await this.httpClient.post<PreInvoice>(
      '/invoices/pre-invoices/merge',
      { pre_invoice_ids: preInvoiceIds }
    );
    return response;
  }

  /**
   * Facturar una prefactura (marcar como CLOSED)
   * @param id - ID de la prefactura
   * @returns Promise con respuesta de la API
   */
  async invoicePreInvoice(id: number): Promise<{ success: boolean; message: string }> {
    try {
      const response = await this.httpClient.post<any>(`/invoices/pre-invoices/${id}/invoice`, {});
      
      // 🎯 Si la API devuelve 200 OK sin la propiedad 'success', considerarlo éxito
      if (response && typeof response.success === 'undefined') {
        return { 
          success: true, 
          message: response.message || 'Prefactura facturada exitosamente'
        };
      }
      
      return response;
    } catch (error: any) {
      // 🎯 Manejar errores de la API
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // La API devuelve el error en errorData.error.message
        if (errorData.error && errorData.error.message) {
          return {
            success: false,
            message: errorData.error.message,
          };
        }
        
        // Si hay un mensaje de error general
        if (errorData.message) {
          return {
            success: false,
            message: errorData.message,
          };
        }
      }
      
      // Error genérico
      return {
        success: false,
        message: error.message || 'Error al facturar la prefactura',
      };
    }
  }

  /**
   * Eliminar una prefactura
   * DELETE /api/v1/invoices/pre-invoices/{id}
   * @param id - ID de la prefactura
   * @returns Promise con respuesta de la API
   */
  async deletePreInvoice(id: number): Promise<{ 
    success: boolean; 
    message: string;
    pre_invoice_id?: number;
    shipments_released?: number;
    storages_released?: number;
  }> {
    try {
      const response = await this.httpClient.delete<any>(`/invoices/pre-invoices/${id}`);
      
      // 🎯 Si la API devuelve 200 OK, considerar éxito
      if (response) {
        return {
          success: true,
          message: response.message || 'Prefactura eliminada exitosamente',
          pre_invoice_id: response.pre_invoice_id,
          shipments_released: response.shipments_released,
          storages_released: response.storages_released,
        };
      }
      
      return {
        success: false,
        message: 'Error al eliminar la prefactura',
      };
    } catch (error: any) {
      // 🎯 Manejar errores de la API
      const status = error?.response?.status;
      
      if (status === 404) {
        return {
          success: false,
          message: 'Prefactura no encontrada',
        };
      }
      
      if (status === 422) {
        const errorData = error.response?.data;
        const errorMessage = errorData?.error || errorData?.message || 'No se puede eliminar una prefactura cerrada que ya ha sido facturada';
        return {
          success: false,
          message: errorMessage,
        };
      }
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // La API devuelve el error en errorData.error o errorData.message
        if (errorData.error) {
          return {
            success: false,
            message: typeof errorData.error === 'string' ? errorData.error : errorData.error.message || 'Error al eliminar la prefactura',
          };
        }
        
        if (errorData.message) {
          return {
            success: false,
            message: errorData.message,
          };
        }
      }
      
      // Error genérico
      return {
        success: false,
        message: error.message || 'Error al eliminar la prefactura',
      };
    }
  }
}




